import React, {Component, ReactNode} from "react"
import PropTypes from "prop-types"
import MobileDetect from "mobile-detect"
import {withRouter} from "react-router-dom"

import {connect} from "@irati/store"
import shouldRedirectToGDPR from "@irati/onboarding/helpers/shouldRedirectToGDPR"
import TermsAndConditions from "@irati-web/onboarding/src/containers/TermsAndConditions"
import SetDetailsModal from "@irati-web/onboarding/src/components/UserOnboarding/SetDetailsModal"
import Header from "@irati-web/platform/src/components/Header"
import {ReduxState} from "@irati/store/types"
import {ADMIN_HOME} from "@irati-web/admin/src/routes/routeConfig"
/* eslint-disable no-restricted-imports */
/** @description: Tech debt, this should be moved outside the ui folder */
import authentication from "@irati/auth"
/* eslint-enable no-restricted-imports */

import hasSetDetails from "@irati/onboarding/helpers/hasSetDetails"
import styled, {css} from "styled-components"
import {HEADER_DESKTOP_HEIGHT, HEADER_MOBILE_HEIGHT} from "@irati-web/platform/src/components/Header/styled"
import Footer from "./Footer"
import Oops from "./Oops"
import MobileDisabled from "./MobileDisabled"
import {
  Main,
  ADMIN_NAVIGATION_SIDEBAR_WIDTH,
  NAVIGATION_SIDEBAR_WIDTH,
  COLLAPSED_SIDEBAR_WIDTH,
  SECONDARY_NAV_COLLAPSED_SIDEBAR_WIDTH
} from "./styled"
import AppNavigation from "../../../platform/src/components/AppNavigation"
import Flex from "../Flex"
import shouldSecondNavigationRender from "./shouldSecondNavigationRender"
import SideBarContext from "./Context/SideBarContext"

interface OptionalProps {
  addContentMarginTop?: boolean,
  disableMobile?: boolean,
  hasFooter?: boolean,
  isFlush?: boolean,
  mainBackground?: string
}

interface CollapsedNavs {
  firstNav: boolean,
  secondNav: boolean
}

interface Props extends OptionalProps, ReduxState {
  children: ReactNode
}

interface State {
  caught: any,
  collapsedNavs: CollapsedNavs
}

interface StyledContentWrapperProps {
  shouldDisplayNavigation?: boolean,
  isFullWidth?: boolean,
  isAdminSection?: boolean,
  width?: number
}

const StyledContentWrapper = styled(Flex)<StyledContentWrapperProps>`
  width: 100%;
  ${({theme, shouldDisplayNavigation, isFullWidth, isAdminSection, width}) => css`
    ${!isAdminSection && `margin-top: ${HEADER_MOBILE_HEIGHT};`}
    ${theme.mediaQueries.minWidthMedia.tablet`
      ${!isAdminSection && `margin-top: ${HEADER_DESKTOP_HEIGHT};`}
      margin-left: ${shouldDisplayNavigation ? `${width}px` : 0};
      width: ${isFullWidth ? "100%" : `calc(100% - ${width}px)`};
    `}
  `}
`

class AppContainer extends Component<Props, State> {
  static defaultProps: OptionalProps = {
    disableMobile: false,
    hasFooter: true,
    isFlush: false
  }

  static contextTypes = {
    appPath: PropTypes.string.isRequired,
    apps: PropTypes.object.isRequired
  }

  state: State = {
    caught: null,
    width: NAVIGATION_SIDEBAR_WIDTH,
    collapsedNavs: {
      firstNav: false,
      secondNav: false
    }
  }

  componentDidMount() {
    const {apps, appPath} = this.context
    const isAdminSection = appPath === apps.ADMIN
    this.setState({width: isAdminSection ? ADMIN_NAVIGATION_SIDEBAR_WIDTH : NAVIGATION_SIDEBAR_WIDTH})
  }

  componentDidUpdate(prevProps) {
    if (this.props.location !== prevProps.location) {
      if (shouldSecondNavigationRender()) {
        return this.handleUpdateNavigationStates({
          firstNav: true,
          secondNav: false
        })
      }
      const {appPath} = this.context
      const pathname = window.location.pathname
      const isHome = pathname === `${appPath}${ADMIN_HOME}`

      this.handleUpdateNavigationStates({
        firstNav: !isHome,
        secondNav: false
      })
    }
  }

  componentDidCatch = (caught: State["caught"]) => {
    this.setState({caught})
  }

  getWidth = () => {
    const {collapsedNavs} = this.state

    if (shouldSecondNavigationRender()) {
      const navOneWidth = collapsedNavs.firstNav ? COLLAPSED_SIDEBAR_WIDTH : ADMIN_NAVIGATION_SIDEBAR_WIDTH
      const navTwoWidth = collapsedNavs.secondNav ? SECONDARY_NAV_COLLAPSED_SIDEBAR_WIDTH : NAVIGATION_SIDEBAR_WIDTH
      return navOneWidth + navTwoWidth
    }
    return collapsedNavs.firstNav ? COLLAPSED_SIDEBAR_WIDTH : ADMIN_NAVIGATION_SIDEBAR_WIDTH
  }

  shouldShowMobileDisabled = () => {
    const {disableMobile, mq} = this.props
    if (!disableMobile) {
      return false
    }

    const detect = new MobileDetect(window.navigator.userAgent)
    return disableMobile && (mq.lessThan.medium || detect.mobile())
  }

  handleUpdateNavigationStates = (collapsedNavs: CollapsedNavs) => {
    this.setState({collapsedNavs})
  }

  render() {
    const {auth, children, isFlush, hasFooter, addContentMarginTop, mainBackground, mq} = this.props
    const {apps, appPath} = this.context
    const {caught, collapsedNavs} = this.state
    const {onboarding, user} = auth
    const isLoggedIn = authentication.isLoggedIn()
    const {ADMIN} = apps
    const isAdminSection = appPath === ADMIN

    if (onboarding && shouldRedirectToGDPR(onboarding) && isLoggedIn) {
      return <TermsAndConditions />
    }

    const showSetDetailsPopup = isLoggedIn && !hasSetDetails(onboarding)
    const isMobile = mq.lessThan.medium
    const shouldDisplayNavigation = isLoggedIn && !isMobile
    const width = appPath === ADMIN
      ? this.getWidth()
      : NAVIGATION_SIDEBAR_WIDTH

    return (
      <>
        <SideBarContext.Provider
          value={{
            navWidth: width
          }}>
          <Flex direction="row">
            <AppNavigation
              shouldDisplay={shouldDisplayNavigation}
              onUpdateNavigationStates={this.handleUpdateNavigationStates}
              collapsedNavs={collapsedNavs} />
            <StyledContentWrapper
              direction="column"
              shouldDisplayNavigation={shouldDisplayNavigation}
              isAdminSection={isAdminSection}
              isFullWidth={!isLoggedIn}
              width={width}>
              <Header />
              <Main
                flush={isFlush}
                marginTop={addContentMarginTop}
                mainBackground={mainBackground}
                className="main"
                width={width}
                isAdminSection={!!isAdminSection}
                shouldDisplayNavigation={shouldDisplayNavigation}>
                {!caught && (this.shouldShowMobileDisabled() ? <MobileDisabled /> : children)}
                {caught && <Oops caught={caught} />}
              </Main>
              {hasFooter && <Footer />}
            </StyledContentWrapper>
          </Flex>
        </SideBarContext.Provider>
        {showSetDetailsPopup && <SetDetailsModal user={user} />}
      </>
    )
  }
}

const mapStateToProps = (state: ReduxState) => {
  return {
    mq: state.mq,
    auth: state.auth
  }
}

export default connect(mapStateToProps)(withRouter(AppContainer))
